import { useNavigator } from '@repo/toolkit/use-navigator'
import { FormIcon } from '@repo/ui/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { createEnrollment } from '../../../api/enrollments/create-enrollment'
import { createEnrollmentProposition } from '../../../api/enrollments/create-enrollment-proposition'
import { createOtoIndividualProposition } from '../../../api/enrollments/create-oto-individual-proposition'
import { getJourney } from '../../../api/products/get-journey'
import UserNavBar from '../../../components/elements/nav-bar'
import CreateEnrollmentForm from '../../../components/pages/enrollments/enrollment-form/create-enrollment-form'
import EnrollmentPropositionInsuranceModal from '../../../components/pages/enrollments/enrollment-proposition-insurance-modal'
import EnrollmentPropositionFamilyArrangementModal from '../../../components/pages/enrollments/enrolment-create-proposition-steps/enrollment-proposition-family-arrangement-modal'
import {
  EnrollmentCreationStateInput,
  mapEnrollmentPropositionInputToRequest,
  mapEnrollmentToCreateEnrollmentInput
} from '../../../components/pages/enrollments/utils/enrollments-utils'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { MedicalConditionEnum } from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { FamilyArrangementEnum } from '../../../lib/types/enums/ol/family-arrangement.enum'
import { CreateEnrollmentInput } from '../../../lib/types/models/enrollment/enrollment-input'
import { mapOtoIndividualPropositionInputToRequest } from '../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { twoButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export const enrollmentInitialValues: Partial<CreateEnrollmentInput> = {
  hasChildHealtInformation: false,
  medicalLevel: MedicalConditionEnum.RAS
}

const EnrollmentCreatePage = () => {
  const { navigate, back } = useNavigator()
  const state = useLocation().state as EnrollmentCreationStateInput

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'select-insurance' | 'select-family-arrangement',
    { values: CreateEnrollmentInput }
  >(['select-insurance', 'select-family-arrangement'])
  const queryClient = useQueryClient()

  const { mutateAsync: createEnrollmentMutation } = useMutation({
    mutationFn: createEnrollment,
    onError: (error) => {
      showAxiosError(error)
      closeModel('select-insurance')
    }
  })

  const { mutateAsync: createEnrollmentPropositionMutation } = useMutation({
    mutationFn: createEnrollmentProposition,
    onError: (error) => {
      showAxiosError(error)
      closeModel('select-insurance')
    }
  })

  const { mutateAsync: createOtoIndividualPropositionMutation } = useMutation({
    mutationFn: createOtoIndividualProposition,
    onError: showAxiosError
  })

  // Create the enrollment first and then the proposition
  const handleSubmit = async (values: CreateEnrollmentInput, additionalEmails?: string[]) => {
    const { proposition, otoIndividualProposition, ...enrollmentData } = values
    const enrollment = await createEnrollmentMutation({
      ...enrollmentData,
      additionalEmails,
      declaredFriends: enrollmentData.declaredFriends ?? [],
      status: ApplicationStatusEnum.INCOMPLETE_REGISTRATION,
      childBirthDate: enrollmentData.childBirthDate
    })

    if (otoIndividualProposition)
      await createOtoIndividualPropositionMutation({
        id: enrollment.id,
        ...mapOtoIndividualPropositionInputToRequest(otoIndividualProposition)
      })
    else if (proposition)
      await createEnrollmentPropositionMutation({
        id: enrollment.id,
        ...mapEnrollmentPropositionInputToRequest(proposition),
        // the CSE registration only exists once the proposition carries the CSE code
        extraOptionLabel: enrollmentData.extraOptionLabel
      })

    toast.success("Demande d'inscription créée avec succès")
    return enrollment
  }

  // a stay offering both FA1E and FA2E needs the family arrangement chosen before creating (e.g. duplicate of a legacy request without one)
  // a stay offering neither rejects one (e.g. duplicate of a request whose stay offered one)
  const handleConfirm = async (values: CreateEnrollmentInput) => {
    if (values.proposition?.journey && !values.otoIndividualProposition) {
      const { id } = values.proposition.journey
      const stay = await queryClient
        .fetchQuery({
          queryKey: ['journey', id],
          queryFn: () => getJourney(id)
        })
        .catch((error: Error) => {
          showAxiosError(error)
          return undefined
        })
      if (!stay) return
      if (!stay.fa1eIncluded && !stay.fa2eIncluded)
        values = {
          ...values,
          proposition: { ...values.proposition, familyArrangement: undefined }
        }
      else if (stay.fa1eIncluded && stay.fa2eIncluded && !values.proposition.familyArrangement) {
        toggleModal('select-family-arrangement', { values })
        return
      }
    }
    toggleModal('select-insurance', { values })
  }

  return (
    <div className="flex flex-col gap-4">
      <UserNavBar icon={FormIcon} title="Créer une nouvelle demande d'inscription" />
      <CreateEnrollmentForm
        defaultValues={
          // a duplicate keeps the CSE of the family it was copied from
          state?.id
            ? mapEnrollmentToCreateEnrollmentInput(state, {
                cseCode: state.cseRegistration?.cse.code
              })
            : enrollmentInitialValues
        }
        buttonsConfig={twoButtonsConfig({
          onConfirm: handleConfirm,
          onCancel: () => back()
        })}
      />

      <EnrollmentPropositionFamilyArrangementModal
        isOpen={modalState['select-family-arrangement']}
        onClose={() => closeModel('select-family-arrangement')}
        onSubmit={(familyArrangement: FamilyArrangementEnum) => {
          if (!selectedData?.values.proposition) return
          const values = {
            ...selectedData.values,
            proposition: { ...selectedData.values.proposition, familyArrangement }
          }
          closeModel('select-family-arrangement')
          toggleModal('select-insurance', { values })
        }}
      />

      <EnrollmentPropositionInsuranceModal
        isOpen={modalState['select-insurance']}
        cseCode={selectedData?.values.proposition?.cseCode}
        residenceCountry={selectedData?.values.country}
        destinationCountry={
          selectedData?.values.proposition?.journey?.date.product.country.id ??
          selectedData?.values.otoIndividualProposition?.product.country.id
        }
        proposition={selectedData?.values.proposition}
        onClose={() => {
          if (!selectedData) return
          closeModel('select-insurance')
        }}
        onSubmit={async (insurances, additionalEmails) => {
          if (!selectedData) return
          const res = await handleSubmit(
            selectedData.values.otoIndividualProposition
              ? {
                  ...selectedData.values,
                  otoIndividualProposition: {
                    ...selectedData.values.otoIndividualProposition,
                    insurances
                  }
                }
              : {
                  ...selectedData.values,
                  proposition: { ...selectedData.values.proposition!, insurances }
                },
            additionalEmails
          ).catch(() => undefined) // the failing mutation already shows its error
          if (!res) return
          closeModel('select-insurance')
          navigate(Paths.ENROLLMENT_UPDATE_PAGE.replace(':id', res.id.toString()))
        }}
      />
    </div>
  )
}

export default EnrollmentCreatePage
